import React, { useEffect, useState } from "react";
import TutorLayout from "./TutorLayout";
import { useAuth } from "./AuthContext";
import { collection, getDocs, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";

function TutorDashboard() {
  const { currentUser } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [studentsMap, setStudentsMap] = useState({});
  const [studentsList, setStudentsList] = useState([]);
  const [showAllSessions, setShowAllSessions] = useState(false);

  // 🔥 NEW: Update user status when this dashboard loads
  useEffect(() => {
    if (!currentUser) return;

    const userRef = doc(db, "users", currentUser.uid);

    // Set status to "online" and log login time
    updateDoc(userRef, {
      status: "online",
      lastLogin: serverTimestamp(),
    });

    // Set status to "offline" when user closes or refreshes tab
    const handleUnload = () => {
      updateDoc(userRef, {
        status: "offline",
      });
    };

    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [currentUser]);

  useEffect(() => {
    const fetchData = async () => {
      const bookingsSnap = await getDocs(collection(db, "bookings"));
      const usersSnap = await getDocs(collection(db, "users"));

      const usersMap = {};
      const students = [];

      usersSnap.docs.forEach((doc) => {
        const data = doc.data();
        const fullName = `${data.displayName || ""} ${data.surname || ""}`.trim();
        usersMap[doc.id] = fullName;
        if (data.role === "student") {
          students.push(fullName);
        }
      });

      const now = new Date();
      const upcoming = bookingsSnap.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((booking) => {
          const sessionDateTime = new Date(`${booking.date}T${booking.time}`);
          return sessionDateTime > now;
        })
        .sort((a, b) => new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`));

      setStudentsMap(usersMap);
      setStudentsList(students);
      setSessions(upcoming);
    };

    fetchData();
  }, []);

  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  };

  const formatTimeRange = (startTimeStr, durationStr = "60") => {
    const [hours, minutes] = startTimeStr.split(":").map(Number);
    const start = new Date();
    start.setHours(hours, minutes);
    const end = new Date(start.getTime() + parseInt(durationStr) * 60000);
    const pad = (n) => n.toString().padStart(2, "0");
    return `${pad(start.getHours())}:${pad(start.getMinutes())} - ${pad(end.getHours())}:${pad(end.getMinutes())}`;
  };

  const subjectEmoji = (subject) => {
    switch (subject) {
      case "GCSE Maths": return "📐";
      case "A-Level Maths": return "📊";
      case "GCSE French": return "🇫🇷";
      case "A-Level French": return "🗼";
      default: return "📘";
    }
  };

  return (
    <TutorLayout>
      <div style={styles.container}>
        <h1>Welcome back, {currentUser?.displayName || "Tutor"}!</h1>
        <p>Here’s what’s happening today 👇</p>

        <div style={styles.layout}>
          {/* Left column: Upcoming Sessions */}
          <div style={styles.leftColumn}>
            <div style={styles.card}>
              <h2>📅 Upcoming Sessions</h2>
              {sessions.length === 0 ? (
                <p>No upcoming sessions.</p>
              ) : (
                <>
                  <ul style={{ paddingLeft: "1rem" }}>
                    {(showAllSessions ? sessions : sessions.slice(0, 5)).map((sesh) => (
                      <li key={sesh.id} style={{ marginBottom: "1rem" }}>
                        <strong>{formatDate(sesh.date)}</strong> —{" "}
                        <strong>{formatTimeRange(sesh.time, sesh.duration)}</strong>{" "}
                        ({sesh.frequency || "one-off"})
                        <div>👤 {studentsMap[sesh.studentId] || "Unknown student"}</div>
                        <div>{subjectEmoji(sesh.subject)} {sesh.subject || "Unknown subject"}</div>
                        {sesh.notes && <div>📝 {sesh.notes}</div>}
                      </li>
                    ))}
                  </ul>
                  {!showAllSessions && sessions.length > 5 && (
                    <button onClick={() => setShowAllSessions(true)} style={styles.showMoreBtn}>
                      Show More
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Right column: My Students & Messages */}
          <div style={styles.rightColumn}>
            <div style={styles.sideCard}>
              <h2>👩‍🎓 My Students</h2>
              <ul style={{ paddingLeft: "1rem", margin: 0 }}>
                {studentsList.map((name, i) => (
                  <li key={i}>📘 {name}</li>
                ))}
              </ul>
            </div>

            <div style={styles.sideCard}>
              <h2>📬 Student Messages</h2>
              <p>You have 2 unread messages.</p>
            </div>
          </div>
        </div>
      </div>
    </TutorLayout>
  );
}

const styles = {
  container: {
    padding: "2rem",
    fontFamily: "'Poppins', sans-serif",
  },
  layout: {
    display: "flex",
    alignItems: "flex-start",
    gap: "2rem",
    marginTop: "1.5rem",
  },
  leftColumn: {
    flex: 1,
  },
  rightColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "2rem",
    width: "1350px",
    flexShrink: 0,
  },
  card: {
    backgroundColor: "#f8f8f8",
    padding: "1.5rem",
    borderRadius: "8px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
  },
  sideCard: {
    backgroundColor: "#fff",
    padding: "1rem 1.25rem",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    fontSize: "0.95rem",
  },
  showMoreBtn: {
    marginTop: "1rem",
    padding: "0.5rem 1rem",
    border: "1px solid #aaa",
    borderRadius: "6px",
    backgroundColor: "#fff",
    cursor: "pointer",
    fontWeight: "500",
  },
};

export default TutorDashboard;
